import groupBy from "lodash/groupBy";
import partition from "lodash/partition";
import { put, select } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import {
  getConsignmentById,
  getCurrentSaleyardPic,
  getSaleLotById,
  getScanByEid,
  selectSaleLotIdByEidLookup,
} from "selectors";

import toast from "lib/toast";

interface BuildP2PLegCreateBulkSagaConfig {
  // Which PIC field is the saleyard's own: "buyer_pic" for In, "vendor_pic"
  // for Out.
  fixedPicField: "buyer_pic" | "vendor_pic";
  // Resolves the other side's PIC (vendor for In, destination for Out).
  resolveVariablePic: (saleLot, consignment, state) => unknown;
  // Dispatched once transfers are built and grouped by sale lot.
  createBulkAction: (files: object[]) => { type: string };
}

function buildTransferGenerator({
  fixedPicField,
  resolveVariablePic,
}: BuildP2PLegCreateBulkSagaConfig) {
  const variablePicField =
    fixedPicField === "buyer_pic" ? "vendor_pic" : "buyer_pic";

  return function generateTransfer(
    saleLotId: string,
    saleLotEids: string[],
    saleyardPic: string | null,
    state,
  ) {
    const saleLot = getSaleLotById(saleLotId)(state);
    const consignment = getConsignmentById(saleLot.consignment_id)(state) || {};

    const transferEids = saleLotEids.map(eid => ({
      EID: eid,
      response_type: "",
      message_no: null,
      action: null,
    }));

    return {
      [fixedPicField]: saleyardPic,
      [variablePicField]: resolveVariablePic(saleLot, consignment, state),
      eids: transferEids,
      freetext: "",
      NVD: consignment.NVD,
    };
  };
}

function buildTransfersGenerator(
  generateTransfer: ReturnType<typeof buildTransferGenerator>,
) {
  return function generateTransfers(
    saleLotId: string,
    eids: string[],
    saleyardPic: string | null,
    state,
  ) {
    const transfers = [];
    const [deceasedEids, liveEids] = partition(
      eids,
      eid => getScanByEid(eid)(state)?.animal.marked_deceased,
    );
    transfers.push(generateTransfer(saleLotId, liveEids, saleyardPic, state));

    if (deceasedEids.length > 0) {
      const deceasedTransfer = generateTransfer(
        saleLotId,
        deceasedEids,
        saleyardPic,
        state,
      );
      // NLIS convention: deceased animals always transfer to DECEASED.
      deceasedTransfer.buyer_pic = "DECEASED";
      transfers.push(deceasedTransfer);
    }
    return transfers;
  };
}

/**
 * Groups EIDs by sale lot, builds per-lot transfer payloads (splitting
 * out deceased animals), and dispatches a bulk-create action. Shared by
 * Export P2P In/Out.
 */
export function buildP2PLegCreateBulkSaga(
  config: BuildP2PLegCreateBulkSagaConfig,
) {
  const generateTransfer = buildTransferGenerator(config);
  const generateTransfers = buildTransfersGenerator(generateTransfer);

  return function* onCreateBulkAction(action: { eids: string[] }) {
    const { eids } = action;
    const state = yield select();
    const saleLotIdByEidLookup = selectSaleLotIdByEidLookup(state);
    const saleyardPic = getCurrentSaleyardPic(state);

    const files: object[] = [];
    const [unlottedEids, lottedEids] = partition(
      eids,
      eid => saleLotIdByEidLookup[eid] === "UNALLOCATED",
    );
    const eidsBySaleLotId = groupBy(
      lottedEids,
      eid => saleLotIdByEidLookup[eid],
    );

    for (const [saleLotId, saleLotEids] of Object.entries(eidsBySaleLotId)) {
      const transfers = generateTransfers(
        saleLotId,
        saleLotEids as string[],
        saleyardPic,
        state,
      );
      files.push({
        id: uuidv4(),
        asynchronous: true,
        pending: true,
        saleLots: [saleLotId],
        transfers,
      });
    }

    yield put(config.createBulkAction(files));

    if (unlottedEids.length > 0) {
      toast.error(`${unlottedEids.length} EIDs were unable to be submitted`);
    }
  };
}
